import {ReadableSpan, Span, SpanProcessor} from '@opentelemetry/sdk-trace-base';
import {FetchCustomAttributeFunction} from '@opentelemetry/instrumentation-fetch';
import {XHRCustomAttributeFunction} from '@opentelemetry/instrumentation-xml-http-request';
import {FetchError} from '@opentelemetry/instrumentation-fetch/build/src/types';
import * as api from '@opentelemetry/api';

import {spans} from 'common/util/tracing';

export class HttpUrlPathProcessor implements SpanProcessor {
  // This should eventually be moved to onEnding if it ever goes stable
  // ref https://github.com/open-telemetry/opentelemetry-specification/pull/4024
  onStart(span: Span): void {
    const candidateAttributes = {
      [`http.url`]: `http.path_pattern`,
      [`page.url`]: `page.path_pattern`,
      [`url.path`]: `url.path_pattern`,
      [`entry_page.path`]: `entry_page.path_pattern`,
    };
    for (const [fromAttrName, toAttrName] of Object.entries(candidateAttributes)) {
      if (span.attributes[fromAttrName]) {
        try {
          const attrValue = String(span.attributes[fromAttrName]);
          let pathName = attrValue;
          if (/^https?:\/\//.test(attrValue)) {
            const url = new URL(attrValue);
            pathName = url.pathname;
          }
          // # Do transforms
          // ## id-like segments
          pathName = pathName.replaceAll(/\/\d+\//g, `/(\\d+)/`);
          pathName = pathName.replaceAll(/\/\d+$/g, `/(\\d+)`);

          // ## asset-cache hashes
          pathName = pathName.replaceAll(/\/static\/asset-cache\/[^/]+\//g, `/static/asset-cache/(hash)/`);

          // ## Public dashboards
          pathName = pathName.replaceAll(
            /\/api\/app\/public\/dashboards\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
            `/api/app/public/dashboards/(uuid)`,
          );
          pathName = pathName.replaceAll(/^\/public\/[^/]+/g, `/public/(id)`);

          span.setAttributes({
            [toAttrName]: pathName,
          });
          // eslint-disable-next-line no-empty
        } catch (_) {}
      }
    }
  }

  onEnd(): void {}

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }
}

/**
 * A SpanProcessor that creates a duplicate span for the auto-instrumented fetch spans and links them
 *
 * This allows us to:
 * - have fetch spans present under the app-level spans to see a user's interaction across their session
 * - have insights into the upstream service spans without the noise of other user interactions
 * - link between a fetch request and the session trace via span links
 */
export class FetchSpanWrapperProcessor implements SpanProcessor {
  onStart(span: Span): void {
    const spanId = span.spanContext()?.spanId;
    const appLevelSpanId = spans.getAppLevelSpanId();
    if (spanId && appLevelSpanId && span?.attributes[`component`] === `fetch`) {
      spans.withSpanById(appLevelSpanId, () => {
        const [duplicateSpan] = spans.getOrCreateSpan({
          id: this.getWrapperSpanId(spanId),
          name: span.name,
          options: {
            kind: api.SpanKind.INTERNAL,
            attributes: {...span.attributes, component: undefined} as api.Attributes,
            links: [{context: span.spanContext()} as api.Link],
            startTime: span.startTime,
            root: false,
          } as api.SpanOptions,
          parentId: appLevelSpanId,
        });

        span.addLink({
          context: duplicateSpan.spanContext(),
        });
      });
    }
  }

  onEnd(span: ReadableSpan): void {
    const spanId = span.spanContext()?.spanId;

    if (spanId && span?.attributes[`component`] === `fetch`) {
      spans.endSpanById(this.getWrapperSpanId(spanId), span.endTime);
    }
  }

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }

  getWrapperSpanId(spanId: string): string {
    return spanId + `-wrapper`;
  }
}

function setHeaderAttribute(span: api.Span, key: string, value: string) {
  // Check if the header starts with X-MP-Query
  if (key.toLowerCase().startsWith(`x-mp-query-`)) {
    // Extract the attribute name by removing the prefix and converting to snake_case
    const attributeName = key.substring(`x-mp-`.length).toLowerCase().replace(/-/g, `_`); // Convert kebab-case to snake_case

    span.setAttribute(attributeName, value);
  }
}

export const applyQueryHeaderAttributesToFetchSpan: FetchCustomAttributeFunction = function (
  span: api.Span,
  _request: Request | RequestInit,
  result: Response | FetchError,
) {
  try {
    if (`headers` in result && result.headers instanceof Headers) {
      const headers = result.headers;

      // Get all header keys
      headers.forEach((value, key) => {
        setHeaderAttribute(span, key, value);
      });
    }
    // eslint-disable-next-line no-empty
  } catch (_) {}
};

export const applyQueryHeaderAttributesToXhrSpan: XHRCustomAttributeFunction = function (
  span: api.Span,
  request: XMLHttpRequest,
) {
  try {
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getAllResponseHeaders
    const headers = request.getAllResponseHeaders();
    if (headers) {
      // Convert the header string into an array
      // of individual headers
      const arr = headers.trim().split(/[\r\n]+/);

      arr.forEach((line) => {
        const parts = line.split(`: `);
        const key = parts.shift();
        const value = parts.join(`: `);
        if (key && value) {
          setHeaderAttribute(span, key, value);
        }
      });
    }
    // eslint-disable-next-line no-empty
  } catch (_) {}
};
